import type {
    TypeMenuItem,
    TypePageFields,
    TypePersonFields,
  } from './generated-types'
  import type { ButtonProps } from '@shc/ui'
  import type { RouteMetadata, MenuItem, MenuItemData } from './types'
  import { contentTypeBasePathMap, indexPageSlug } from './constants'
  import type * as Contentful from 'contentful'
  
  export const isInternalHref = (href: string | undefined): boolean => {
    if (typeof href === 'undefined' || href === '') {
      return false
    }
    // https://jex.im/regulex/#!flags=i&re=%5E(%3F!(%3F%3Aht%7Cf)tp%7C%3A%7C%5C%2F%5C%2F)%5B%5E.%5D*(%5C%2F.*)%3F%24
    return href.match(/^(?!(?:ht|f)tp|:|\/\/)[^.]+(\/.*)?$/i) !== null
  }
  
  export const routeMapper = (
    link?: Contentful.Entry<TypePageFields | TypePersonFields>,
    anchorOrExternalLink?: string
  ): RouteMetadata => {
    let isInternal = false
    let isAppInternal = false
    let route = ''
  
    if (link) {
      const basePath = contentTypeBasePathMap[link.sys?.contentType.sys.id]
      const slug = link.fields?.slug === indexPageSlug ? '' : link.fields?.slug
      route = basePath + slug
      isInternal = true
      isAppInternal = true
    } else {
      route = anchorOrExternalLink ?? ''
      isInternal = isInternalHref(route)
    }
  
    return {
      route,
      isInternal,
      isAppInternal,
    }
  }
  
  export const menuItemMapper = (value: TypeMenuItem): MenuItem => {
    // Early return when `fields` is not defined; https://github.com/contentful/contentful.js/issues/427
    if (!value.fields) return [{ id: '', name: '', route: '', isInternal: false }, []]
  
    const children: MenuItem[] = value.fields.items ? value.fields.items.map(menuItemMapper) : []
  
    const { route, isInternal, isAppInternal } = routeMapper(
      value.fields.link,
      value.fields.anchorOrExternalLink
    )
  
    const data: MenuItemData = {
      id: value.sys.id,
      name: value.fields.title,
      route,
      isInternal,
      isAppInternal,
    }
  
    /**
     * NextJS barfs on serializing undefined :face-palm:
     * we will be careful not to add the key if the value is undefined
     * https://github.com/vercel/next.js/discussions/11209#discussioncomment-1745088
     *
     * For MVP people will be typing in icon names so let
     * us handle accidental whitespace.
     */
    if (
      typeof value.fields.icon !== 'undefined' &&
      typeof value.fields.icon.fields.icon === 'string' &&
      value.fields.icon.fields.icon.trim().length > 0
    ) {
      data.icon = value.fields.icon.fields.icon.replace(/\s/g, '').trim()
    }
  
    return [data, children]
  }

  /**
   * Returns link attributes for a button or anchor element based on Contentful entry and external link.
   * Determines if the link is internal or external and sets appropriate target and rel attributes.
   *
   * @param link - Contentful entry for the link (topic, article, page, or person)
   * @param anchorOrExternalLink - Optional anchor or external URL string
   * @returns ButtonProps for the link, or undefined if no valid route
   */
  export const linkAttributes = (
    link?: Contentful.Entry<TypeNews2knowArticleFields | TypePageFields | TypeProviderPageFields>,
    anchorOrExternalLink?: string
  ): ButtonProps | undefined => {
    const { route, isInternal } = routeMapper(link, anchorOrExternalLink)

    if (route === '') {
      return undefined
    }

    return {
      href: route,
      target: isInternal ? undefined : '_blank',
      rel: isInternal ? undefined : 'noopener noreferrer',
    }
  }
  
  export default menuItemMapper
  